/**
 * TryoutListUploadModal — Drag&Drop CSV → preview → confirm flow.
 * Mounted from AdminTryoutTokensView via "Lead-Liste hochladen" button.
 */
import { useCallback, useState } from 'react';
import { Loader2, Upload, AlertCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { parseLeadCsv, type ParsedLead } from '@/lib/csv/leadCsvParser';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (listId: string) => void;
}

type Phase = 'pick' | 'preview' | 'submitting';

export const TryoutListUploadModal: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const [phase, setPhase] = useState<Phase>('pick');
  const [error, setError] = useState<string | null>(null);

  const [rows, setRows] = useState<ParsedLead[]>([]);
  const [skipped, setSkipped] = useState(0);
  const [detectedCampaign, setDetectedCampaign] = useState<string | null>(null);

  const [listName, setListName] = useState('');
  const [campaignTag, setCampaignTag] = useState('');
  const [source, setSource] = useState<'cold_email' | 'manual' | 'landing_page' | 'internal_test'>('cold_email');
  const [maxUses, setMaxUses] = useState(3);
  const [runCheck, setRunCheck] = useState(true);

  const reset = useCallback(() => {
    setPhase('pick'); setError(null); setRows([]); setSkipped(0);
    setDetectedCampaign(null); setListName(''); setCampaignTag('');
    setSource('cold_email'); setMaxUses(3); setRunCheck(true);
  }, []);

  const handleFile = useCallback(async (file: File) => {
    setError(null);
    const text = await file.text();
    const parsed = parseLeadCsv(text);
    if (parsed.errors.length > 0) {
      setError(parsed.errors.join(' • '));
      return;
    }
    if (parsed.rows.length === 0) {
      setError('Keine gültigen Zeilen gefunden.');
      return;
    }
    setRows(parsed.rows);
    setSkipped(parsed.skippedCount);
    setDetectedCampaign(parsed.detectedCampaignName);
    setListName(parsed.detectedCampaignName ?? file.name.replace(/\.csv$/i, ''));
    setPhase('preview');
  }, []);

  const handleSubmit = useCallback(async () => {
    setPhase('submitting'); setError(null);

    const supaUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      setError('Nicht eingeloggt.');
      setPhase('preview'); return;
    }

    const res = await fetch(`${supaUrl}/functions/v1/tryout-list-create`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        list_name: listName.trim(),
        campaign_tag: campaignTag.trim() || null,
        source,
        campaign_name: detectedCampaign,
        max_uses: maxUses,
        base_url: window.location.origin,
        run_personalization_check: runCheck,
        leads: rows,
      }),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      setError(body.error ?? `Upload fehlgeschlagen (HTTP ${res.status})`);
      setPhase('preview');
      return;
    }

    const { list_id } = await res.json();
    reset();
    onCreated(list_id);
  }, [listName, campaignTag, source, detectedCampaign, maxUses, runCheck, rows, onCreated, reset]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Lead-Liste aus CSV erstellen</DialogTitle></DialogHeader>

        {phase === 'pick' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Lade eine Instantly-Export CSV (Format: Estage). Pflicht-Spalten:
              E-Mail, Vorname, Nachname, companyName, personalization.
            </p>
            <label className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 cursor-pointer hover:bg-muted/50">
              <Upload className="h-8 w-8 mb-2 text-muted-foreground" />
              <span className="text-sm">CSV-Datei hier hinziehen oder klicken</span>
              <input type="file" accept=".csv" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
            </label>
            {error && (
              <div className="flex gap-2 items-start rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
              </div>
            )}
          </div>
        )}

        {phase === 'preview' && (
          <div className="space-y-4">
            <div className="rounded-md border p-3 text-sm space-y-1">
              <div><strong>{rows.length}</strong> gültige Leads erkannt
                {skipped > 0 && <span className="text-muted-foreground"> · {skipped} übersprungen (fehlende Pflichtfelder)</span>}
              </div>
              {detectedCampaign && <div className="text-muted-foreground">Kampagne in CSV: <code>{detectedCampaign}</code></div>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Listenname</Label>
                <Input value={listName} onChange={(e) => setListName(e.target.value)} />
              </div>
              <div>
                <Label>Tag (optional)</Label>
                <Input value={campaignTag} onChange={(e) => setCampaignTag(e.target.value)}
                  placeholder="z.B. Hausverwaltungen Sachsen" />
              </div>
              <div>
                <Label>Source</Label>
                <select className="w-full border rounded-md h-9 px-2"
                  value={source} onChange={(e) => setSource(e.target.value as typeof source)}>
                  <option value="cold_email">Cold E-Mail</option>
                  <option value="manual">Manuell</option>
                  <option value="landing_page">Landing Page</option>
                  <option value="internal_test">Interner Test</option>
                </select>
              </div>
              <div>
                <Label>max_uses pro Token</Label>
                <Input type="number" min={1} max={50} value={maxUses}
                  onChange={(e) => setMaxUses(Number(e.target.value))} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={runCheck} onCheckedChange={setRunCheck} id="check-toggle" />
              <Label htmlFor="check-toggle">Personalisierung per KI prüfen (im Hintergrund)</Label>
            </div>
            {error && (
              <div className="flex gap-2 items-start rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => { reset(); onClose(); }}>Abbrechen</Button>
          {phase === 'preview' && (
            <Button onClick={handleSubmit} disabled={!listName.trim()}>
              {rows.length} Tokens erstellen
            </Button>
          )}
          {phase === 'submitting' && (
            <Button disabled><Loader2 className="h-4 w-4 mr-2 animate-spin" />Erstelle…</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
